import React, { useCallback } from 'react';
import { TextField, Grid, useMediaQuery } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import ReactQuill from 'react-quill';
import tokens from '../../../theme/tokens';
import components from '../../../theme/components';
import 'react-quill/dist/quill.bubble.css';
import { styled, useTheme } from '@mui/material/styles';

const DateFieldComponent = ({ label, value, onChange, ...props }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <DatePicker
      label={label}
      views={['year', 'month']}
      value={value}
      onChange={onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          sx={{
            ...components.input.root,
            '& .MuiInputBase-root': {
              height: tokens.spacing.input.height,
              minHeight: tokens.spacing.input.height,
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiOutlinedInput-input': {
              padding: tokens.spacing.input.padding,
              height: 'auto',
            },
            '& .MuiInputLabel-root': {
              transform: 'translate(14px, 16px) scale(1)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -6px) scale(0.75)',
              }
            }
          }}
          inputProps={{
            ...params.inputProps,
            readOnly: true,
            style: {
              fontSize: tokens.typography.fontSizes.xs,
              padding: 0,
            }
          }}
        />
      )}
      {...props}
    />
  );
};

export const DateField = React.memo(DateFieldComponent);

const StyledQuillWrapper = styled('div')(({ theme, isMobile }) => (`
  .ql-container {
    min-height: ${isMobile ? '100px' : '150px'};
    border-radius: ${tokens.radius.base};
    border-color: ${theme.palette.mode === 'dark' 
      ? tokens.colors.borders.dark 
      : tokens.colors.borders.light};
  }
  .ql-toolbar {
    border-radius: ${tokens.radius.base};
    background-color: ${theme.palette.mode === 'dark' 
      ? tokens.colors.background.dark 
      : tokens.colors.background.light};
    border-color: ${theme.palette.mode === 'dark' 
      ? tokens.colors.borders.dark 
      : tokens.colors.borders.light};
    ${isMobile ? `
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      padding: 4px;
    ` : ''}
  }
  .ql-editor {
    min-height: ${isMobile ? '100px' : '150px'};
    font-family: ${tokens.typography.fontFamily.primary};
    font-size: ${isMobile ? tokens.typography.fontSizes.base : tokens.typography.fontSizes.sm};
    color: ${theme.palette.mode === 'dark' 
      ? tokens.colors.text.dark
      : tokens.colors.text.light};
    background-color: ${theme.palette.mode === 'dark' 
      ? tokens.colors.background.dark 
      : tokens.colors.background.light};
  }
  ${isMobile ? `
    .ql-toolbar button {
      width: 28px;
      height: 28px;
      padding: 2px;
    }
  ` : ''}
`));

// Wrap the RichTextField functional component for memoization
const RichTextFieldComponent = ({ value, onChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleQuillChange = useCallback((content, delta, source, editor) => {
    if (source === 'user') {
      onChange(content);
    }
  }, [onChange]);

  return (
    <StyledQuillWrapper className="rich-text-editor" isMobile={isMobile}>
      <ReactQuill
        value={value || ''}
        onChange={handleQuillChange}
        modules={{
          toolbar: [
            [{ header: [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ color: [] }, { background: [] }],
            ['clean']
          ],
        }}
        theme="bubble"
        style={{
          height: isMobile ? '100px' : '150px',
          marginBottom: isMobile ? tokens.spacing[6] : tokens.spacing[10],
          border: `1px solid ${tokens.colors.borders.default}`,
        }}
      />
    </StyledQuillWrapper>
  );
};

// Export the memoized version
export const RichTextField = React.memo(RichTextFieldComponent);

// Define base styles reusable for different input types if needed
const baseInputStyles = (theme, isMobile) => ({
  backgroundColor: theme.palette.mode === 'dark'
    ? tokens.colors.background.dark
    : tokens.colors.background.light,
  height: 'auto', // Let content determine height
  minHeight: isMobile ? '48px' : tokens.spacing.input.height, // Minimum height
  display: 'flex',
  alignItems: 'center', // Vertically center content
  marginBottom: tokens.spacing.input.marginBottom, // Consistent bottom margin
  '& fieldset': {
    borderColor: theme.palette.mode === 'dark'
      ? tokens.colors.borders.dark
      : tokens.colors.borders.light,
  },
  // Input element itself
  '& .MuiInputBase-input': { // Target the input element more specifically
    fontSize: isMobile ? tokens.typography.fontSizes.base : tokens.typography.fontSizes.xs,
    padding: isMobile ? `12px 12px` : tokens.spacing.input.padding, // Adjust mobile padding slightly for centering
    height: 'auto', // Remove fixed height
    boxSizing: 'border-box', // Ensure padding is included in height calculation
  },
  // Adjust padding for multiline TextFields
  '& .MuiInputBase-inputMultiline': {
    padding: isMobile ? `12px 12px` : '0', // Remove default padding for multiline on desktop
  },
});

const baseLabelStyles = (theme, isMobile) => ({
  fontSize: isMobile ? tokens.typography.fontSizes.sm : tokens.typography.fontSizes.xs,
  // Adjust label positioning based on input padding and font size
  transform: isMobile ? 'translate(12px, 14px) scale(1)' : `translate(14px, ${tokens.spacing.input.height / 2 - 8}px) scale(1)`, // Center label vertically on desktop
  '&.MuiInputLabel-shrink': {
    transform: isMobile ? 'translate(12px, -9px) scale(0.75)' : 'translate(14px, -9px) scale(0.75)', // Consistent shrink transform
  }
});

// Create a single styled TextField component
export const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'isMobile' // Prevent isMobile from reaching DOM
})(({ theme, isMobile }) => ({
  '& .MuiOutlinedInput-root': baseInputStyles(theme, isMobile),
  '& .MuiInputLabel-root': baseLabelStyles(theme, isMobile),
  // Apply margin directly to the root for consistency
  marginBottom: isMobile ? tokens.spacing[2] : tokens.spacing.input.marginBottom,
}));

// Enhanced ResponsiveTextField using the StyledTextField
export const ResponsiveTextField = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Use the single styled component, passing the isMobile prop
  return (
    <StyledTextField
      {...props}
      fullWidth // Apply fullWidth by default, can be overridden by props
      isMobile={isMobile} // Pass isMobile to the styled component
      sx={{
         // Keep the ability to pass custom sx props
        ...(props.sx || {}),
      }}
    />
  );
};

export const FormGridContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'isMobile'
})(({ theme, isMobile }) => ({
  '& .MuiGrid-item': {
    paddingTop: `${isMobile ? tokens.spacing[1] : tokens.spacing.input.gap} !important`,
    paddingBottom: `0 !important`,
  },
}));